import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import QRCode from 'qrcode';


const PreviewModal = ({ isOpen, onClose, dealership }) => {
  if (!isOpen) return null;

  const landingPageUrl = dealership?.urlSlug 
    ? `https://www.gridbalance.de/${dealership?.urlSlug}`
    : null;

  const downloadQRCode = async () => {
    if (!landingPageUrl) return;

    try {
      // Generate QR code with consistent styling matching the uploaded template
      const qrCodeDataUrl = await QRCode?.toDataURL(landingPageUrl, {
        width: 600,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H'
      });

      // Create download link
      const link = document.createElement('a');
      link.href = qrCodeDataUrl;
      link.download = `gridbalance-qr-${dealership?.urlSlug || 'dealership'}.png`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    } catch (error) {
      console.error('QR Code generation failed:', error);
      alert('QR-Code konnte nicht generiert werden');
    }
  };

  // Generate QR code for display
  const [qrCodeUrl, setQrCodeUrl] = React.useState('');

  React.useEffect(() => {
    if (landingPageUrl) {
      QRCode?.toDataURL(landingPageUrl, {
        width: 600,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H'
      })?.then(url => setQrCodeUrl(url))?.catch(err => console.error('QR Code generation failed:', err));
    }
  }, [landingPageUrl]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-border">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Eye" className="text-primary" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold text-foreground">
                Partner Vorschau
              </h2>
              <p className="text-sm text-muted-foreground">
                {dealership?.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Landing Page URL with QR Code */}
          {landingPageUrl && (
            <div className="bg-muted/50 rounded-lg p-6 border border-border">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Link" size={18} className="text-primary" />
                    <h3 className="font-semibold text-foreground">Landing Page URL</h3>
                  </div>
                  <div className="bg-background rounded-lg p-3 mb-4 border border-border">
                    <p className="text-sm text-foreground font-mono break-all">
                      {landingPageUrl}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        navigator.clipboard?.writeText(landingPageUrl);
                        alert('URL in Zwischenablage kopiert!');
                      }}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth text-sm font-medium flex items-center gap-2"
                    >
                      <Icon name="Copy" size={16} />
                      URL kopieren
                    </button>
                    <a
                      href={landingPageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth text-sm font-medium flex items-center gap-2 border border-border"
                    >
                      <Icon name="ExternalLink" size={16} />
                      Öffnen
                    </a>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <div 
                    id="stylistic-qr-container"
                    className="bg-white p-2 rounded-2xl border-2 border-border shadow-lg relative overflow-hidden"
                    style={{ width: '200px', height: '200px' }}
                  >
                    {qrCodeUrl ? (
                      <img
                        src={qrCodeUrl}
                        alt="Grid Balance QR Code"
                        className="w-full h-full object-contain"
                        style={{
                          width: '100%',
                          height: '100%'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="Loader2" className="animate-spin text-muted-foreground" size={24} />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground text-center font-medium">
                    Grid Balance QR-Code für {dealership?.name}
                  </p>
                  <button
                    onClick={downloadQRCode}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth text-sm font-medium flex items-center gap-2 shadow-sm"
                  >
                    <Icon name="Download" size={16} />
                    QR-Code herunterladen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Dealership Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Logo Display */}
            {dealership?.logo ? (
              <div className="bg-muted/50 rounded-lg p-4 border border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Image" size={16} />
                  Logo
                </h3>
                <div className="bg-white rounded-lg p-4 flex items-center justify-center min-h-[120px] border border-border">
                  <img
                    src={dealership?.logo}
                    alt={dealership?.logoAlt || `${dealership?.name} Logo`}
                    className="max-w-full max-h-[100px] object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="flex flex-col items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg><p class="text-xs text-muted-foreground">Logo konnte nicht geladen werden</p></div>';
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-muted/50 rounded-lg p-4 border border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Image" size={16} />
                  Logo
                </h3>
                <div className="bg-white rounded-lg p-4 flex flex-col items-center justify-center min-h-[120px] border border-border">
                  <Icon name="Building2" size={40} color="var(--color-muted-foreground)" />
                  <p className="text-xs text-muted-foreground mt-2">Kein Logo vorhanden</p>
                </div>
              </div>
            )}

            {/* Basic Info */}
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Building2" size={16} />
                Basisinformationen
              </h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Icon name="MapPin" size={14} className="text-muted-foreground mt-1 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-foreground">{dealership?.street}</p>
                    <p className="text-foreground">{dealership?.postalCode} {dealership?.city}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={14} className="text-muted-foreground flex-shrink-0" />
                  <p className="text-sm text-foreground">{dealership?.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Phone" size={14} className="text-muted-foreground flex-shrink-0" />
                  <p className="text-sm text-foreground">{dealership?.phone}</p>
                </div>
                {dealership?.website && (
                  <div className="flex items-center gap-2">
                    <Icon name="Globe" size={14} className="text-muted-foreground flex-shrink-0" />
                    <a
                      href={dealership?.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      {dealership?.website}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* CI Colors */}
            {(dealership?.ciPrimaryColor || dealership?.ciSecondaryColor) && (
              <div className="bg-muted/50 rounded-lg p-4 border border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Palette" size={16} />
                  Corporate Identity
                </h3>
                <div className="space-y-3">
                  {dealership?.ciPrimaryColor && (
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-border shadow-sm"
                        style={{ backgroundColor: dealership?.ciPrimaryColor }}
                      />
                      <div>
                        <p className="text-xs text-muted-foreground">Primärfarbe</p>
                        <p className="text-sm font-mono text-foreground">{dealership?.ciPrimaryColor}</p>
                      </div>
                    </div>
                  )}
                  {dealership?.ciSecondaryColor && (
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-lg border-2 border-border shadow-sm"
                        style={{ backgroundColor: dealership?.ciSecondaryColor }}
                      />
                      <div>
                        <p className="text-xs text-muted-foreground">Sekundärfarbe</p>
                        <p className="text-sm font-mono text-foreground">{dealership?.ciSecondaryColor}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contact Person */}
            {dealership?.contactPerson && (
              <div className="bg-muted/50 rounded-lg p-4 border border-border">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="User" size={16} />
                  Ansprechpartner
                </h3>
                <p className="text-sm text-foreground">{dealership?.contactPerson}</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {dealership?.notes && (
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="FileText" size={16} />
                Notizen
              </h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {dealership?.notes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;